"use client";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import GetBrandsService from "@/http/api/brand/GetBrandsServices";
import { useRouter } from "next/navigation";
import Spinner from "@/components/ui/loading/Spinner";
import CategoryServices from "@/http/api/category/CategoryServices";

const { getBrandById } = new GetBrandsService();
const { getAttributeOptionTitle, getVariationOptionTitle } = new CategoryServices();

const FilterButtons = ({ searchParams }: { searchParams: Record<string, any> }) => {
    const [brandNames, setBrandNames] = useState<Record<string, string>>({});
    const [variationNames, setVariationNames] = useState<Record<string, string>>({});
    const [attributeNames, setAttributeNames] = useState<Record<string, string>>({});
    const router = useRouter();

    const filterKeys = ["brands[]", "variations[]", "attributes[]"];
    const isFilterApplied = filterKeys.some((key) => searchParams.hasOwnProperty(key));
    const isAvailableFilter = searchParams.hasOwnProperty("available");
    const isMinPrice = searchParams.hasOwnProperty("min_price");
    const isMaxPrice = searchParams.hasOwnProperty("max_price");

    useEffect(() => {
        const fetchFilterData = async () => {
            if (searchParams["brands[]"] && typeof searchParams["brands[]"] === "string") {
                const brandFilters = searchParams["brands[]"].split(",").map((item: string) => item.trim());
                try {
                    const brandPromises = brandFilters.map(async (id: string) => {
                        const brand = await getBrandById(Number(id));
                        return { id, title: brand?.data?.title || `Brand ${id}` };
                    });
                    const resolvedBrands = await Promise.all(brandPromises);
                    const brandMap = resolvedBrands.reduce(
                        (acc, { id, title }) => {
                            acc[id] = title;
                            return acc;
                        },
                        {} as Record<string, string>,
                    );
                    setBrandNames(brandMap);
                } catch (error) {
                    console.error("Error fetching brand data:", error);
                }
            }

            if (searchParams["variations[]"] && typeof searchParams["variations[]"] === "string") {
                const variationFilters = searchParams["variations[]"].split(",").map((item: string) => item.trim());
                try {
                    const variationPromises = variationFilters.map(async (id: string) => {
                        const { data }: any = await getVariationOptionTitle(id);
                        return { id: data?.id, title: data?.title };
                    });
                    const resolvedVariations = await Promise.all(variationPromises);
                    const variationMap = resolvedVariations.reduce(
                        (acc, { id, title }) => {
                            acc[id] = title;
                            return acc;
                        },
                        {} as Record<string, string>,
                    );
                    setVariationNames(variationMap);
                } catch (error) {
                    console.error("Error fetching variation data:", error);
                }
            }

            if (searchParams["attributes[]"] && typeof searchParams["attributes[]"] === "string") {
                const attributeFilters = searchParams["attributes[]"].split(",").map((item: string) => item.trim());
                try {
                    const attributePromises = attributeFilters.map(async (id: string) => {
                        const { data }: any = await getAttributeOptionTitle(id);
                        return { id: data?.id, title: data?.title };
                    });
                    const resolvedAttributes = await Promise.all(attributePromises);
                    const attributeMap = resolvedAttributes.reduce(
                        (acc, { id, title }) => {
                            acc[id] = title;
                            return acc;
                        },
                        {} as Record<string, string>,
                    );
                    setAttributeNames(attributeMap);
                } catch (error) {
                    console.error("Error fetching attribute data:", error);
                }
            }
        };

        fetchFilterData();
    }, [searchParams]);

    const removeFilterHandler = (param: string, value?: string) => {
        const params = new URLSearchParams();
        // مقادیر searchParams رو به URLSearchParams منتقل کنید
        Object.entries(searchParams).forEach(([key, val]) => {
            if (Array.isArray(val)) {
                val.forEach((v) => params.append(key, v));
            } else if (val) {
                params.set(key, val);
            }
        });

        if (filterKeys.includes(param)) {
            const items = params.get(param)?.split(",") || [];
            const updatedItems = items.filter((item) => item !== value);
            updatedItems.length ? params.set(param, updatedItems.join(",")) : params.delete(param);
        } else {
            params.delete(param);
        }
        router.push(`?${params.toString()}`);
    };

    return (
        <div className="p-2 px-5 flex flex-wrap gap-2 items-center">
            {!isFilterApplied && !isAvailableFilter && !isMinPrice && !isMaxPrice ? null : (
                <>
                    {Object.entries(searchParams).map(([key, value], index) => {
                        if (filterKeys.includes(key)) {
                            const values = Array.isArray(value)
                                ? value
                                : typeof value === "string"
                                    ? value.split(",")
                                    : [];
                            return values.map((id: any) => (
                                <Button
                                    onClick={() => removeFilterHandler(key, id)}
                                    variant="outline"
                                    className="rounded-lg text-xs flex items-center gap-1 text-global-text-color"
                                    key={`${key}-${id}`}
                                >
                                    <X width={19} />
                                    {key === "brands[]" && (brandNames[id] || <Spinner />)}
                                    {key === "variations[]" && (variationNames[id] || <Spinner />)}
                                    {key === "attributes[]" && (attributeNames[id] || <Spinner />)}
                                </Button>
                            ));
                        }

                        if (key === "min_price" && value != 0) {
                            return (
                                <Button
                                    onClick={() => removeFilterHandler(key)}
                                    variant="outline"
                                    className="rounded-lg text-xs flex items-center gap-1 text-global-text-color"
                                    key={`price-${key}`}
                                >
                                    <X width={19} />
                                    {`بالای: ${Number(value).toLocaleString("fa")} تومان`}
                                </Button>
                            );
                        }

                        if (key === "max_price" && value !== 0) {
                            return (
                                <Button
                                    onClick={() => removeFilterHandler(key)}
                                    variant="outline"
                                    className="rounded-lg text-xs flex items-center gap-1 text-global-text-color"
                                    key={`price-${key}`}
                                >
                                    <X width={19} />
                                    {`زیر: ${Number(value).toLocaleString("fa")} تومان`}
                                </Button>
                            );
                        }

                        if (isAvailableFilter && key === "available" && value === "true") {
                            return (
                                <Button
                                    onClick={() => removeFilterHandler(key)}
                                    variant="outline"
                                    className="rounded-lg text-xs flex items-center gap-1 text-global-text-color"
                                    key={index}
                                >
                                    <X width={19} />
                                    موجود
                                </Button>
                            );
                        }
                    })}
                </>
            )}
        </div>
    );
};

export default FilterButtons;